import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import WhatIfCalculator from "@/components/WhatIfCalculator";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

const HERO_IMG = "https://images.unsplash.com/photo-1493397212122-2b85dda8106b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SUB_IMG = "https://images.unsplash.com/photo-1567943183748-3a7542120c90?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const { t } = useTranslation();
  const { fmt: fmtMoney } = useCurrency();
  const [stats, setStats] = useState({ icos: 0, total_raised: 0, total_distributed: 0, yield_index: 0, active_offerings: 0 });

  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div className="bg-white text-black">
      {/* HERO */}
      <section className="relative border-b border-black/10 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 pt-20 pb-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 fade-up">
            <div className="font-mono-label text-[11px] text-[#002FA7] mb-6">⎯⎯ {t("hero_tag")}</div>
            <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tight">
              {t("hero_title_1")} <span className="text-[#002FA7]">{t("hero_title_2")}</span>.
            </h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-xl">{t("hero_sub")}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta-start" className="rounded-none bg-black hover:bg-zinc-800 h-12 px-7 font-mono-label text-xs">
                  {t("hero_cta_start")}
                </Button>
              </Link>
              <a href="#calculator">
                <Button data-testid="hero-cta-calc" variant="outline" className="rounded-none border-black h-12 px-7 font-mono-label text-xs">
                  {t("hero_cta_calc")}
                </Button>
              </a>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5 fade-up d-2">
            <div className="aspect-[4/5] w-full overflow-hidden border border-black/10">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            </div>

            {/* Live yield index — primary stat */}
            <div data-testid="hero-yield-index" className="mt-4 bg-[#002FA7] text-white p-5 border border-[#002FA7]">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-mono-label text-[10px] text-[#FFCC00]">{t("stat_yield_index")}</div>
                  <div className="font-display text-4xl font-black mt-1">
                    {stats.yield_index ? `${stats.yield_index.toFixed(2)}%` : "—"}
                  </div>
                </div>
                <div className="text-end">
                  <div className="font-mono-label text-[10px] text-zinc-300">{t("stat_active_offerings")}</div>
                  <div className="font-display text-3xl font-bold mt-1">{stats.active_offerings || 0}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 mt-2 border border-black/10">
              <div className="p-4 border-e border-black/10">
                <div className="font-mono-label text-[10px] text-zinc-500">{t("stat_raised")}</div>
                <div className="font-display text-2xl font-bold">{fmtMoney(stats.total_raised)}</div>
              </div>
              <div className="p-4">
                <div className="font-mono-label text-[10px] text-zinc-500">{t("stat_distributed")}</div>
                <div className="font-display text-2xl font-bold">{fmtMoney(stats.total_distributed)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-black/10 lm-grid-bg">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="font-mono-label text-[11px] text-[#002FA7] mb-4">⎯⎯ {t("how_tag")}</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-3xl">{t("how_title")}</h2>

          <div className="grid grid-cols-12 gap-6 mt-16">
            {[
              { tag: t("how_01_tag"), title: t("how_01_title"), body: t("how_01_body") },
              { tag: t("how_02_tag"), title: t("how_02_title"), body: t("how_02_body") },
              { tag: t("how_03_tag"), title: t("how_03_title"), body: t("how_03_body") },
              { tag: t("how_04_tag"), title: t("how_04_title"), body: t("how_04_body") },
            ].map((c, i) => (
              <div key={c.tag} className={`col-span-12 md:col-span-3 border border-black p-8 bg-white hover:-translate-y-1 transition-transform fade-up d-${i+1}`}>
                <div className="font-mono-label text-[10px] text-[#002FA7]">{c.tag}</div>
                <div className="font-display text-xl font-bold mt-6">{c.title}</div>
                <p className="text-zinc-600 mt-4 text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT-IF CALCULATOR */}
      <WhatIfCalculator />

      {/* TRUST */}
      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-6">
            <img src={SUB_IMG} alt="" className="w-full aspect-square object-cover border border-black/10" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="font-mono-label text-[11px] text-[#002FA7] mb-4">⎯⎯ {t("why_tag")}</div>
            <h3 className="font-display text-4xl md:text-5xl font-black tracking-tight">{t("why_title")}</h3>
            <ul className="mt-8 space-y-4 text-zinc-700">
              {[t("why_b1"), t("why_b2"), t("why_b3"), t("why_b4")].map((s) => (
                <li key={s} className="flex gap-3 text-sm">
                  <span className="text-[#002FA7] mt-1">▸</span><span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* ─── FOUNDING TEAM ─── */}
      <section className="border-b border-black/10 bg-white" data-testid="founding-team-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="font-mono-label text-[11px] text-[#002FA7] mb-4">⎯⎯ {t("team_tag")}</div>
          <div className="grid grid-cols-12 gap-8 items-end">
            <h3 className="col-span-12 md:col-span-7 font-display text-4xl md:text-5xl font-black tracking-tight">
              {t("team_title")}
            </h3>
            <p className="col-span-12 md:col-span-5 text-zinc-600 text-sm leading-relaxed">
              {t("team_sub")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10 mt-12">
            {/* Founder 1 — Abdullah Al-Nami */}
            <a
              href="https://www.linkedin.com/in/bnnami/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="team-member-founder"
              className="bg-white p-10 group transition hover:bg-zinc-50"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 bg-black text-[#FFCC00] flex items-center justify-center font-display text-2xl font-black shrink-0">
                  AN
                </div>
                <div className="min-w-0">
                  <div className="font-mono-label text-[10px] text-[#002FA7]">{t("team_1_role")}</div>
                  <h4 className="font-display text-2xl font-black mt-1 tracking-tight">
                    {t("team_1_name")}
                  </h4>
                </div>
              </div>
              <p className="text-zinc-600 text-sm leading-relaxed mt-5">
                {t("team_1_bio")}
              </p>
              <div className="mt-6 font-mono-label text-[10px] text-black group-hover:text-[#002FA7] transition flex items-center gap-2">
                <span className="inline-block w-4 h-px bg-current" />
                {t("team_1_link")}
              </div>
            </a>

            {/* Founder 2 — Dr. Adnan Al-Zahrani */}
            <a
              href="https://eservice.sba.gov.sa/directory/370"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="team-member-counsel"
              className="bg-white p-10 group transition hover:bg-zinc-50"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 bg-[#002FA7] text-white flex items-center justify-center font-display text-2xl font-black shrink-0">
                  AZ
                </div>
                <div className="min-w-0">
                  <div className="font-mono-label text-[10px] text-[#002FA7]">{t("team_2_role")}</div>
                  <h4 className="font-display text-2xl font-black mt-1 tracking-tight">
                    {t("team_2_name")}
                  </h4>
                </div>
              </div>
              <p className="text-zinc-600 text-sm leading-relaxed mt-5">
                {t("team_2_bio")}
              </p>
              <div className="mt-6 font-mono-label text-[10px] text-black group-hover:text-[#002FA7] transition flex items-center gap-2">
                <span className="inline-block w-4 h-px bg-current" />
                {t("team_2_link")}
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#002FA7] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <h3 className="font-display text-4xl md:text-6xl font-black tracking-tight">
              {t("cta_title_1")}<br/>{t("cta_title_2")}
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end items-end">
            <Link to="/register" className="w-full md:w-auto">
              <Button data-testid="footer-cta" className="rounded-none w-full bg-white text-black hover:bg-[#FFCC00] h-14 px-8 font-mono-label text-xs">
                {t("cta_btn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 flex justify-between items-center text-xs text-zinc-500">
          <span className="font-mono-label">© 2026 {t("brand")}</span>
          <span className="font-mono-label">v1.0 · simulated chain</span>
        </div>
      </footer>
    </div>
  );
}
